import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Loader2, Pencil } from 'lucide-react';
import clsx from 'clsx';
import type { AngleGroup } from '@/hooks/useSessionWatchData';

// Mirrors the inline shape other call sites (MyPage, useAuth) use to read a
// backend error code off an axios rejection — there is no shared ApiError
// type with a `code` field in this codebase to import instead.
interface RenameApiError {
  response?: {
    data?: {
      error?: {
        code?: string;
      };
    };
  };
}

export interface AngleSwitcherProps {
  groups: AngleGroup[];
  activeFragmentId: string | null;
  onSelectFragment: (fragmentId: string) => void;
  /**
   * `'dark'` (default) matches the public /watch page's chrome. `'light'`
   * restyles chips for the (white-background) session detail page. The
   * player itself is unaffected — only chip/label colors change.
   */
  variant?: 'dark' | 'light';
  /**
   * Renders groups with no playable fragment as non-interactive "processing"
   * chips (dashed border, spinner) instead of omitting them. Off by default
   * so the public page keeps hiding angles that aren't ready yet; the owner
   * variant on the session detail page turns this on.
   */
  showUnplayable?: boolean;
  /**
   * When provided, every playable chip shows a pencil affordance on
   * hover/focus. Clicking it swaps the chip for an inline text input (Enter
   * commits, Escape/blur cancels). A rejected promise surfaces as an inline
   * message under the chip row — a 409 collision
   * (`error.response.data.error.code === 'ANGLE_NAME_TAKEN'`) gets a
   * specific message, anything else a generic one. Absent by default so the
   * public page never shows the rename affordance.
   */
  onRenameAngle?: (group: AngleGroup, newName: string) => Promise<void>;
}

// A group with a confirmed playable fragment — narrows firstPlayableFragment
// from `string | null` to `string` so callers below don't need a redundant
// null check right before using it as the click target.
type PlayableAngleGroup = AngleGroup & { firstPlayableFragment: string };

function hasPlayableFragment(group: AngleGroup): group is PlayableAngleGroup {
  return group.firstPlayableFragment !== null;
}

// Recordings default to a pristine "Cam N" angleName when no custom name was
// set on the device; that default gets localized. Any operator-supplied name
// (including one that happens to look like "Cam 3" after a rename — treated
// the same as the pristine default, matching the task-9 brief's intent) is
// shown verbatim. A null name (no angleName at all) falls back to "Unnamed".
function localizeAngleName(name: string | null, t: TFunction): string {
  if (name === null) return t('watch.angleUnnamed');
  const match = name.match(/^Cam (\d+)$/);
  if (match) return t('watch.angleCam', { num: match[1] });
  return name;
}

// A group's identity for React keys and edit-state tracking: its shared
// angleName, or (for a null-named single-fragment fallback group) its lone
// fragment's id — mirrors how useSessionWatchData keys groups internally.
function groupKey(group: AngleGroup): string {
  return group.name ?? group.fragments[0]?.id ?? '';
}

function renameErrorMessage(error: unknown, t: TFunction): string {
  const code = (error as RenameApiError)?.response?.data?.error?.code;
  return code === 'ANGLE_NAME_TAKEN'
    ? t('watch.angleNameTaken')
    : t('watch.renameFailed');
}

/**
 * Chip row for switching between camera angles on the public /watch VOD
 * player (Task 10 renders this alongside WatchPlayer) and, since the session
 * detail page embedded the same composition, for the owner-facing detail
 * page too (via `variant`/`showUnplayable`/`onRenameAngle`). With every new
 * prop absent, rendering is unchanged from the original public-only
 * behavior: hidden whenever there is nothing to switch to (fewer than two
 * angle groups have a playable fragment), and groups without a playable
 * fragment are omitted entirely rather than shown disabled.
 */
export function AngleSwitcher({
  groups,
  activeFragmentId,
  onSelectFragment,
  variant = 'dark',
  showUnplayable = false,
  onRenameAngle,
}: AngleSwitcherProps) {
  const { t } = useTranslation('pages');
  const isLight = variant === 'light';

  // Which group (by groupKey) is currently showing its rename input, the
  // text being edited, whether a commit is in flight, and the last commit
  // error (shown under the whole row, not per chip — a rename can only be
  // in flight for one chip at a time).
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);

  const playableGroups = groups.filter(hasPlayableFragment);
  const unplayableCount = groups.length - playableGroups.length;

  // Default: hidden unless there's actually something to switch between.
  // Relaxed when showUnplayable is set — the owner still needs to see a
  // "processing" chip even for a session with only one angle so far, but a
  // single fully-playable angle still renders nothing extra.
  const shouldRender = showUnplayable
    ? groups.length >= 2 || unplayableCount >= 1
    : playableGroups.length > 1;

  if (!shouldRender) return null;

  const itemsToRender = showUnplayable ? groups : playableGroups;

  const startEditing = (group: PlayableAngleGroup) => {
    setRenameError(null);
    setEditValue(localizeAngleName(group.name, t));
    setEditingKey(groupKey(group));
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const commitEditing = async (group: PlayableAngleGroup) => {
    const trimmed = editValue.trim();
    // An unchanged commit must be a cancel, not a rename: the input is
    // prefilled with the LOCALIZED display name ("캠 1" for a stored
    // "Cam 1"), so passing it through would persist the localized string
    // verbatim and detach the group from pristine-name localization.
    if (!trimmed || trimmed === localizeAngleName(group.name, t)) {
      cancelEditing();
      return;
    }
    if (!onRenameAngle) return;

    setIsRenaming(true);
    try {
      await onRenameAngle(group, trimmed);
      cancelEditing();
    } catch (error) {
      setRenameError(renameErrorMessage(error, t));
      cancelEditing();
    } finally {
      setIsRenaming(false);
    }
  };

  return (
    <div className="mt-5">
      <div
        className={clsx(
          'mb-2 text-xs font-semibold uppercase tracking-wide',
          isLight ? 'text-secondary-500' : 'text-slate-500',
        )}
      >
        {t('watch.angles')}
      </div>
      <div
        role="group"
        aria-label={t('watch.angles')}
        className="flex flex-wrap items-center gap-2"
      >
        {itemsToRender.map((group) => {
          if (!hasPlayableFragment(group)) {
            return (
              <span
                key={groupKey(group)}
                className={clsx(
                  'flex items-center gap-1.5 rounded-full border border-dashed px-4 py-2 text-sm font-medium',
                  isLight
                    ? 'border-secondary-300 text-secondary-400'
                    : 'border-white/20 text-slate-500',
                )}
              >
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                <span>{localizeAngleName(group.name, t)}</span>
                <span className="opacity-75">
                  · {t('watch.angleProcessing')}
                </span>
              </span>
            );
          }

          const isActive = group.fragments.some(
            (fragment) => fragment.id === activeFragmentId,
          );
          const key = groupKey(group);
          const chipClassName = clsx(
            'flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors',
            isActive
              ? 'border-blue-600 bg-blue-600 text-white'
              : isLight
                ? 'border-secondary-200 bg-secondary-50 text-secondary-600 hover:bg-secondary-100'
                : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10',
          );
          const metaClassName = clsx(
            'text-xs',
            isActive
              ? 'text-blue-100'
              : isLight
                ? 'text-secondary-400'
                : 'text-slate-500',
          );

          if (!onRenameAngle) {
            return (
              <button
                key={key}
                type="button"
                aria-pressed={isActive}
                onClick={() => {
                  // A click on the already-active chip is a no-op — it must
                  // never restart playback of the fragment already on screen.
                  if (!isActive) onSelectFragment(group.firstPlayableFragment);
                }}
                className={chipClassName}
              >
                <span>{localizeAngleName(group.name, t)}</span>
                {group.fragments.length > 1 && (
                  <span className={metaClassName}>
                    {t('watch.partCount', { count: group.fragments.length })}
                  </span>
                )}
              </button>
            );
          }

          if (editingKey === key) {
            return (
              <div
                key={key}
                className={clsx(
                  'flex items-center rounded-full border px-3 py-1.5 text-sm font-medium',
                  isLight
                    ? 'border-blue-400 bg-white'
                    : 'border-blue-400 bg-white/10',
                )}
              >
                <input
                  autoFocus
                  type="text"
                  value={editValue}
                  disabled={isRenaming}
                  placeholder={t('watch.renameAnglePlaceholder')}
                  aria-label={t('watch.renameAngle')}
                  onChange={(event) => setEditValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void commitEditing(group);
                    } else if (event.key === 'Escape') {
                      event.preventDefault();
                      cancelEditing();
                    }
                  }}
                  onBlur={() => {
                    if (!isRenaming) cancelEditing();
                  }}
                  className={clsx(
                    'w-28 bg-transparent outline-none',
                    isLight ? 'text-secondary-900' : 'text-white',
                  )}
                />
              </div>
            );
          }

          return (
            <div key={key} className="group relative flex items-center">
              <button
                type="button"
                aria-pressed={isActive}
                onClick={() => {
                  if (!isActive) onSelectFragment(group.firstPlayableFragment);
                }}
                className={chipClassName}
              >
                <span>{localizeAngleName(group.name, t)}</span>
                {group.fragments.length > 1 && (
                  <span className={metaClassName}>
                    {t('watch.partCount', { count: group.fragments.length })}
                  </span>
                )}
              </button>
              <button
                type="button"
                aria-label={t('watch.renameAngle')}
                onClick={() => startEditing(group)}
                className={clsx(
                  'ml-1 flex items-center justify-center rounded-full p-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100',
                  isLight
                    ? 'text-secondary-400 hover:text-secondary-700'
                    : 'text-slate-400 hover:text-slate-200',
                )}
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
      {renameError && (
        <p
          role="alert"
          className={clsx(
            'mt-2 text-xs',
            isLight ? 'text-red-600' : 'text-red-400',
          )}
        >
          {renameError}
        </p>
      )}
    </div>
  );
}

export default AngleSwitcher;
